import { useEffect, useMemo, useRef } from 'react'
import { BooleanParam, JsonParam, NumberParam, StringParam, useQueryParams } from 'use-query-params'

import { Nullable } from '@types'

import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import savedSearchApi from '@modules/savedSearch/api'
import { SavedSearch } from '@modules/savedSearch/types'
import searchApi from '@modules/search/api'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { mapApiGroupedProfileTypeToUI, mapApiProfileTypeToUI } from '@shared/utils'
import { UseQueryResult, keepPreviousData, useQuery } from '@tanstack/react-query'

import {
  DEFAULT_PAGE_LIMIT,
  DEFAULT_SORT_OPTION,
  NEW_HIGHLIGHT_MAX_DEPTH,
  SEARCH_GROUP_BY_OPTIONS,
  SORT_SEARCH_OPTIONS,
} from '../constants'
import {
  AnyResultProfile,
  FiltersSetup,
  GroupedResultProfile,
  ProfileType,
  SearchApiResponse,
  SearchProfilesResponse,
  SelectedFiltersMapByProfiles,
  SelectedMetaFilters,
  SortOrder,
  SortType,
} from '../types'

type UseSearchProfilesResponse = UseQueryResult<
  SearchApiResponse<SearchProfilesResponse> & {
    isResultGrouped?: boolean
  },
  unknown
> & {
  rawSearchParams: SearchRequestBody
}

export interface SearchRequestBody {
  types: { type: string; filters?: unknown; isSelected: boolean }[]
  size: number
  from: number
  general: unknown
  codes?: number[]
  query?: string
  sort?: { type: SortType; order: SortOrder }
  groupBy?: ProfileType
  filters?: {
    dateFrom?: string
    dateTo?: string
    excludeIds?: (number | string)[]
    excludeEventIds?: (number | string)[]
    includeCompanyIds?: (number | string)[]
    excludeCompanyIds?: (number | string)[]
  }
}

interface UseSearchProfilesProps {
  selectedSavedSearch?: SavedSearch | null
  searchString: string
  selectedFiltersByProfiles: SelectedFiltersMapByProfiles
  from: number
  codes?: number[]
  searchableEntities: ProfileType[]
  isSearchAllProfilesChecked: boolean
  selectedNewItemsFilter?: boolean
  searchableProfilesMap: Record<string, boolean>
  mergeSearchableFilters?: (filtersSetup: FiltersSetup[]) => void
  eventCodes?: Nullable<number[]>
  selectedSortId?: number
  metaFilters?: SelectedMetaFilters
  selectedGroupingId?: number
  savedSearchId?: Nullable<number>
  pageLimit?: number
  enabled?: boolean
  queryKey?: string[] | string
  eventsFilterData?: Nullable<{ includeIds: number[]; excludeIds: number[] }>
  excludeIds?: (number | string)[]
  onSearchSuccess?: () => void
  selectedEvent?: number
  companyHistoryFilter?: {
    companyIds?: number[]
    exclude?: boolean
  }
}

export const useFormattedSearchParams = ({
  searchString,
  eventCodes,
  selectedFiltersByProfiles,
  from,
  searchableEntities,
  isSearchAllProfilesChecked,
  searchableProfilesMap,
  selectedSortId,
  metaFilters,
  selectedGroupingId,
  pageLimit = DEFAULT_PAGE_LIMIT,
  eventsFilterData,
  excludeIds,
  companyHistoryFilter,
}: UseSearchProfilesProps) => {
  const { user } = useAuthContext()
  const sort = useMemo(() => {
    if (searchString) {
      return null
    }
    if (!selectedSortId) {
      return DEFAULT_SORT_OPTION.data
    }

    return SORT_SEARCH_OPTIONS.find(i => i.id === selectedSortId)?.data
  }, [selectedSortId, searchString])

  const groupBy = useMemo(() => {
    if (!selectedGroupingId) return null

    return SEARCH_GROUP_BY_OPTIONS.find(option => option.id === selectedGroupingId)?.value
  }, [selectedGroupingId])

  const filtersQuery: {
    type: string
    filters?: {
      field: string
      value: string
    }[]
    isSelected: boolean
  }[] = useMemo(
    () =>
      Object.values(ProfileType).map(profile => ({
        type: profile,
        filters: selectedFiltersByProfiles[profile],
        isSelected: searchableProfilesMap[profile] || isSearchAllProfilesChecked,
      })),
    [selectedFiltersByProfiles, searchableProfilesMap, isSearchAllProfilesChecked],
  )
  let searchableProfilesQueryList = filtersQuery

  if (!searchableProfilesQueryList.length && searchableEntities?.length) {
    searchableProfilesQueryList = searchableEntities.map(type => ({ type, isSelected: true }))
  } else if (!searchableProfilesQueryList.length) {
    searchableProfilesQueryList = Object.keys(searchableProfilesMap).map(type => ({ type, isSelected: true }))
  } else {
    Object.keys(searchableProfilesMap).forEach(type => {
      if (
        (searchableProfilesMap[type] || isSearchAllProfilesChecked) &&
        !searchableProfilesQueryList.find(query => query.type === type)
      ) {
        searchableProfilesQueryList.push({
          type,
          isSelected: true,
        })
      }
    })
  }

  const data: SearchRequestBody = useMemo(() => {
    const types = searchableProfilesQueryList.some(i => i.isSelected)
      ? searchableProfilesQueryList
      : searchableProfilesQueryList.map(i => ({ ...i, isSelected: true }))

    const initial: SearchRequestBody = {
      types,
      size: pageLimit,
      from,
      general: filtersQuery.filter(i => i.type === 'general').flatMap(i => i.filters),
    }

    if (eventCodes?.length || metaFilters?.eventCode) {
      // @ts-expect-error jjj
      initial.codes = eventCodes || [metaFilters?.eventCode]
    }

    if (searchString) {
      initial.query = searchString
    }

    if (sort) {
      initial.sort = sort
    }

    if (groupBy) {
      initial.groupBy = groupBy
    }

    if (metaFilters?.dateFrom) {
      initial.filters = { dateFrom: metaFilters.dateFrom }

      if (metaFilters.dateTo) {
        initial.filters.dateTo = metaFilters.dateTo
      }
    }

    if (excludeIds) {
      initial.filters = { ...(initial.filters ?? {}), excludeIds }
    }

    if (companyHistoryFilter?.companyIds) {
      // a hotfix for companies without a history, -1 will search for unexisting profile and return nothing.
      const companyIds = companyHistoryFilter.companyIds?.length ? companyHistoryFilter.companyIds : [-1]
      const usersCompany = user?.companyId as number

      initial.filters = {
        ...(initial.filters ?? {}),
        excludeCompanyIds: companyHistoryFilter.exclude ? [...companyIds, usersCompany] : [usersCompany],
        includeCompanyIds: !companyHistoryFilter.exclude ? companyIds.filter(i => i !== usersCompany) : undefined,
      }
    }

    if (eventsFilterData?.includeIds) {
      initial.codes = eventsFilterData?.includeIds
    }

    if (eventsFilterData?.excludeIds) {
      initial.filters = { ...(initial.filters ?? {}), excludeEventIds: eventsFilterData?.excludeIds }
    }

    return initial
  }, [
    searchableProfilesQueryList,
    metaFilters,
    filtersQuery,
    searchString,
    sort,
    groupBy,
    eventCodes,
    pageLimit,
    from,
    companyHistoryFilter?.companyIds,
    companyHistoryFilter?.exclude,
    eventsFilterData,
    excludeIds,
    user?.companyId,
  ])

  return { ...data, filtersQuery }
}

export const useSearch = (props: UseSearchProfilesProps) => {
  const {
    selectedGroupingId,
    enabled = true,
    queryKey = searchApi.search.key,
    mergeSearchableFilters,
    onSearchSuccess,
    eventsFilterData,
    companyHistoryFilter,
  } = props
  const groupingId = useRef(selectedGroupingId)

  const shouldKeepPreviousData = useMemo(() => {
    const shouldKeep = Boolean((groupingId.current && selectedGroupingId) || (!groupingId.current && !selectedGroupingId))
    groupingId.current = selectedGroupingId
    return shouldKeep
  }, [groupingId, selectedGroupingId])

  const { filtersQuery, ...data } = useFormattedSearchParams(props)

  const isParamsReady = Boolean((filtersQuery && filtersQuery.length > 0) || companyHistoryFilter || eventsFilterData)

  const debouncedQueryData = useDebouncedValue(JSON.stringify(data), 150)

  const debouncedEventsFilters = useDebouncedValue(eventsFilterData, 150)

  const result = useQuery({
    queryKey: [queryKey, debouncedQueryData, debouncedEventsFilters],
    queryFn: () => {
      return searchApi.search.fn(data as unknown as Record<string, unknown>)
    },
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    select: d => ({
      ...d,
      result: !data.groupBy
        ? (mapApiProfileTypeToUI(d.result as AnyResultProfile[]) as SearchProfilesResponse)
        : (mapApiGroupedProfileTypeToUI(d.result as GroupedResultProfile[]) as SearchProfilesResponse),
      isResultGrouped: Boolean(data.groupBy),
    }),
    enabled: enabled && isParamsReady,
  })

  useEffect(() => {
    if (result.data) {
      const { facets } = result.data as SearchApiResponse<SearchProfilesResponse>
      if (onSearchSuccess) {
        onSearchSuccess()
      }

      /** @ts-expect-error will be fixed soon */
      mergeSearchableFilters?.(facets)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data])

  return { result, groupBy: data.groupBy, filtersQuery, sort: data.sort, rawSearchParams: data }
}

export const useSearchProfiles = (params: UseSearchProfilesProps): UseSearchProfilesResponse => {
  const {
    searchString,
    eventCodes,
    from,
    selectedSavedSearch,
    searchableEntities,
    mergeSearchableFilters,
    metaFilters,
    selectedNewItemsFilter,
    savedSearchId,
    selectedGroupingId,
  } = params
  const searchParams = useSearchParamsForSavedSearch()
  const { hasAccessToSavedSearch } = useFeatureFlags()
  const { user } = useAuthContext()
  const { data: rawSavedSearchViewedProfiles = [] } = useQuery({
    queryKey: [savedSearchApi.runSavedSearch.key, savedSearchId, user?.id],
    queryFn: () => savedSearchApi.runSavedSearch.fn(savedSearchId!, searchParams),
    enabled:
      hasAccessToSavedSearch && !!savedSearchId && !!searchParams.savedSearchId && searchParams.savedSearchId === savedSearchId,
  })

  const savedSearchViewedProfiles = rawSavedSearchViewedProfiles.map(i => String(i))

  if (selectedNewItemsFilter) {
    // eslint-disable-next-line
    params.excludeIds = savedSearchViewedProfiles

    if (!selectedSavedSearch?.newItemsCount) {
      // eslint-disable-next-line
      params.pageLimit = 0
    }
  }

  const { result, groupBy, filtersQuery, sort, rawSearchParams } = useSearch(params)

  let queryResponse = { ...result }

  if (!groupBy) {
    const resultProfiles = queryResponse.data?.result as AnyResultProfile[]

    const data = {
      ...queryResponse.data,
      result: resultProfiles?.map((profile, idx) => ({
        ...profile,
        _isViewed:
          !savedSearchId || savedSearchViewedProfiles?.includes(String(profile.id)) || from + idx > NEW_HIGHLIGHT_MAX_DEPTH,
      })),
    }
    // @ts-expect-error ???
    queryResponse = { ...queryResponse, data }
  } else {
    const resultProfiles = queryResponse.data?.result as GroupedResultProfile[]

    const data = {
      ...queryResponse.data,
      result: resultProfiles?.map(groupedData => ({
        ...groupedData,
        profiles: groupedData.profiles?.map((profile, idx) => ({
          ...profile,
          _isViewed:
            !savedSearchId || savedSearchViewedProfiles?.includes(String(profile.id)) || from + idx > NEW_HIGHLIGHT_MAX_DEPTH,
        })),
      })),
    }

    // @ts-expect-error ???
    queryResponse = { ...queryResponse, data }
  }

  useEffect(() => {
    mergeSearchableFilters?.(queryResponse.data?.facets as unknown as FiltersSetup[])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchString,
    eventCodes,
    filtersQuery,
    from,
    searchableEntities,
    sort,
    metaFilters,
    selectedGroupingId,
    selectedNewItemsFilter,
  ])
  /**
   * we use keepPreviousData, it helps to avoid UI jumping on loading states,
   * but if there is no loading we don't want to show previous data except counts
   * */
  const shouldPersistOutdated = useRef(false)
  const isOutDateResult = queryResponse.isPlaceholderData && (!queryResponse.isFetching || shouldPersistOutdated.current)

  if (isOutDateResult) {
    const overrideData = { ...queryResponse.data, result: [], total: 0 }
    shouldPersistOutdated.current = true
    return { ...queryResponse, data: overrideData, rawSearchParams } as UseSearchProfilesResponse
  }

  shouldPersistOutdated.current = false

  return { ...queryResponse, rawSearchParams }
}

export const useSearchParamsForSavedSearch = () => {
  const [query] = useQueryParams({
    sort_by: NumberParam,
    group_by: NumberParam,
    page: NumberParam,
    query: StringParam,
    filters: JsonParam,
    expanded_filter: JsonParam,
    profiles: JsonParam,
    all_profiles: BooleanParam,
    meta_filter: JsonParam,
    saved_search_name: StringParam,
    saved_search_id: NumberParam,
    search_by_history: NumberParam,
    eventsFilter: JsonParam,
  })

  const searchableEntities = useMemo(
    () =>
      Object.keys(query.profiles ?? {}).filter(
        type => query.all_profiles || !!query.profiles[type as ProfileType],
      ) as ProfileType[],
    [query.profiles, query.all_profiles],
  )

  const { filtersQuery: _, ...rest } = useFormattedSearchParams({
    searchString: query.query ?? '',
    selectedFiltersByProfiles: query.filters || {},
    from: 0,
    searchableEntities,
    isSearchAllProfilesChecked: !!query.all_profiles,
    searchableProfilesMap: query.profiles ?? {},
    selectedSortId: query.sort_by ?? undefined,
    metaFilters: query.meta_filter,
    selectedGroupingId: query.group_by ?? undefined,
    eventsFilterData: query.eventsFilter,
  })

  return {
    ...rest,
    searchByHistory: query.search_by_history,
    metaFilters: query.meta_filter,
    savedSearchId: query.saved_search_id,
  }
}
